import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CssBaseline,
  StyledEngineProvider,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AXPAppThemeOptions } from "@/theme/AppTheme";
import {
  getMuiLocales,
  loadDayjsLocale,
  toDayjsLocale,
  type MuiThemeLocaleFragment,
} from "@/utils/muiLocale";
import { buildGridLocaleText } from "@/utils/gridLocaleText";

type MaterialProviderProps = Readonly<{
  children?: React.ReactNode;
}>;

function MUIProvider(props: MaterialProviderProps) {
  const { children } = props;
  const { t, i18n } = useTranslation();
  const [muiLocales, setMuiLocales] = useState<MuiThemeLocaleFragment[]>([]);
  const [dayjsLocale, setDayjsLocale] = useState("en");

  useEffect(() => {
    let cancelled = false;
    const resolved = toDayjsLocale(i18n.language);

    loadDayjsLocale(resolved)
      .then((loaded) => {
        if (!cancelled) setDayjsLocale(loaded ? resolved : "en");
      })
      .catch(() => {
        if (!cancelled) setDayjsLocale("en");
      });

    getMuiLocales(i18n.language).then((locales) => {
      if (!cancelled) setMuiLocales(locales);
    });

    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const theme = createTheme(AXPAppThemeOptions, ...muiLocales, {
    components: {
      MuiDataGrid: {
        defaultProps: {
          localeText: buildGridLocaleText(t),
        },
      },
    },
  });

  return (
    <StyledEngineProvider injectFirst>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={dayjsLocale}
      >
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </LocalizationProvider>
    </StyledEngineProvider>
  );
}

export default MUIProvider;
